import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    Output,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { combineLatest, Observable, Subject, of } from 'rxjs';
import { LoginService } from '@intranet/modules/login/login.service';
import { UserService } from '@intranet/modules/users/user.service';
import {
    take,
    tap,
    takeUntil,
    debounceTime,
    distinctUntilChanged,
    switchMap,
} from 'rxjs/operators';
import { JobService, JobSearchQuery } from '@intranet/modules/jobs';
import { FormGroup } from '@angular/forms';
import { User } from '@intranet/modules/users/user.types';
import { Client, ClientsService } from '@intranet/modules/clients';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';

@Component({
    selector: 'jobs-search',
    templateUrl: './jobs-search.component.html',
    styleUrls: ['./jobs-search.component.scss'],
})
export class JobsSearchComponent implements OnInit, OnChanges {
    @ViewChild('form', { static: true }) form: FormGroup;
    @Output() filter = new EventEmitter<JobSearchQuery>();
    @Output() ownersLoading = new EventEmitter<boolean>();
    @Input() userId: string = '';
    @Input() ownerFromUrl: boolean = false;
    @Input() showAllOwners: boolean = false;
    @Input() clients: Client[];
    @Input() selectedClient: number | null = null;
    @Input() availableOwners: User[] = [];
    @Input() restrictOwners: boolean = false;

    filterClients: [Client[], Client[]] = [[], []];
    isGroup: boolean = false;
    firstLoad: boolean = true;
    filterQuery: JobSearchQuery;
    activeUsers: User[] = [];
    otherUsers: User[] = [];

    // Loading state for the owner filter
    isLoadingOwners: boolean = true;

    // Phones: the filters other than Search are folded away behind a
    // "Filters" button (see the template and stylesheet) — open or not.
    filtersOpen = false;

    // The currently selected user for display (may not be in dropdown options)
    selectedOwner: User | null = null;

    // Store all users for filtering
    private _allUsers: User[] = [];

    // Subject to trigger re-fetching of job owners when client changes
    // Emits { clientId, clientIds } where clientId is a single client and clientIds is for group filtering
    private _clientChanged$ = new Subject<{ clientId?: number; clientIds?: number[] }>();

    // Cache for "all owners" (no client filter) to avoid re-fetching
    private _allOwnersCache: string[] | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _clientService: ClientsService,
        private _userService: UserService,
        private _jobService: JobService,
        private _loginService: LoginService,
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        // React to userId or selectedClient input changes after initialization
        if (this.filterQuery) {
            if (changes['userId'] && !changes['userId'].firstChange) {
                this.filterQuery.userId = this.userId;
                this._updateSelectedOwnerDisplay();
                this._cdr.detectChanges();
            }
            if (changes['selectedClient'] && !changes['selectedClient'].firstChange) {
                this.filterQuery.clientId = this.selectedClient;
                this._cdr.detectChanges();
            }
        }
    }

    ngOnInit(): void {
        this.filterQuery = {
            page: 1,
            sortBy: 'type',
            sortDir: 'desc',
            clients: [],
            active: 'All',
            clientId: this.selectedClient !== null ? this.selectedClient : null,
            userId: this.userId, // defaults to current user from LoginService
        };

        // Ensure "All" is selected by default when selectedClient is null
        if (this.selectedClient !== null) {
            this.filterQuery.clientId = this.selectedClient;
        } else {
            this.filterQuery.clientId = null;  // Ensure "All" is selected
        }

        let observables$: Observable<any>[] = [];
        observables$.push(this._userService.users$);
        observables$.push(this._jobService.filterQuery$);

        // If we are passed in a list of client, this must be a client group (otherwise clients are not passed in)
        if (this.clients && this.clients.length) {
            this.isGroup = this.clients.length > 1;
        } else {
            observables$.push(this._clientService.clients$);
            observables$.push(this._clientService.myClients$);
        }

        combineLatest(observables$)
            .pipe(take(1))
            .subscribe(([users, filterQuery, allClients, myClients]) => {
                // Store all users
                this._allUsers = users;

                // Set initial query values
                this.filterQuery = {
                    ...filterQuery,
                    userId: this.userId,
                    clientId: this.selectedClient !== null ? this.selectedClient : '',
                };
                // Clients
                if (allClients && myClients) {
                    const [pinnedClients, restClients] =
                        orderClientsByGroupName(
                            allClients,
                            myClients.map((c: Client) => c.clientId),
                            true
                        );

                    this.filterClients = [
                        pinnedClients as Client[],
                        restClients as Client[],
                    ];
                }

                // Check if we have cached job owners (for main Jobs page with no client filter)
                const isMainJobsPage = !this.selectedClient && (!this.clients || this.clients.length === 0);
                const cachedOwners = isMainJobsPage ? this._jobService.getCachedJobOwners() : null;

                if (cachedOwners) {
                    // Use cached data immediately - no loading state needed
                    this._allOwnersCache = cachedOwners;
                    this._filterUsers(cachedOwners);

                    // Pre-select current user if they have jobs (only on first visit, not when restored from URL or when showAllOwners is true)
                    if (!this.userId && !this.ownerFromUrl && !this.showAllOwners) {
                        const currentUserId = this._loginService.userId;
                        if (cachedOwners.includes(currentUserId)) {
                            this.filterQuery.userId = currentUserId;
                        }
                    }

                    this.isLoadingOwners = false;
                    this.ownersLoading.emit(false);
                    this._cdr.detectChanges();
                    this.filter.emit(this.filterQuery);
                } else {
                    // No cache - need to fetch job owners
                    // Show loading state while we wait (both local and parent)
                    this.isLoadingOwners = true;
                    this.ownersLoading.emit(true);
                    this._cdr.detectChanges();

                    // Use selectedClient if provided, or get owners for all clients in the group
                    const initialClientId = this.selectedClient ? Number(this.selectedClient) : undefined;
                    const groupClientIds = (this.clients && this.clients.length > 0 && !initialClientId)
                        ? this.clients.map((c: Client) => c.clientId)
                        : undefined;

                    this._jobService.getJobOwners(initialClientId, groupClientIds)
                        .pipe(take(1))
                        .subscribe((owners) => {
                            // Cache all owners on initial load (when no client filter)
                            if (isMainJobsPage) {
                                this._allOwnersCache = owners.userIds;
                            }

                            // Filter users based on job owners
                            this._filterUsers(owners.userIds);

                            // Pre-select current user if they have jobs (only on first visit, not when restored from URL or when showAllOwners is true)
                            if (!this.userId && !this.ownerFromUrl && !this.showAllOwners) {
                                const currentUserId = this._loginService.userId;
                                if (owners.userIds.includes(currentUserId)) {
                                    this.filterQuery.userId = currentUserId;
                                }
                            }

                            this.isLoadingOwners = false;
                            this.ownersLoading.emit(false);
                            this._cdr.detectChanges();
                            this.filter.emit(this.filterQuery);
                        });
                }
            });

        // Only sync with the job service's stored filter query on the main Jobs page.
        // In link-job mode (showAllOwners), we manage filterQuery locally so the
        // selectedClient and "All" owner defaults are not overwritten.
        if (!this.showAllOwners) {
            this._jobService.filterQuery$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    tap((filterQuery) => {
                        this.filterQuery = { ...filterQuery };
                    })
                )
                .subscribe();
        }

        // Watch for client changes and re-fetch job owners
        this._clientChanged$
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(({ clientId, clientIds }) => {
                    // Use cache when switching to "All" (no client filter) on main Jobs page
                    if (!clientId && !clientIds && this._allOwnersCache) {
                        return of({ userIds: this._allOwnersCache });
                    }
                    return this._jobService.getJobOwners(clientId, clientIds);
                })
            )
            .subscribe((owners) => {
                this._filterUsers(owners.userIds);
                this.isLoadingOwners = false;
            });

        this.form.valueChanges
            .pipe(debounceTime(400), distinctUntilChanged())
            .subscribe((values) => {
                // Don't emit while loading owners - the client change handler already emits
                if (!this.isLoadingOwners) {
                    this.filter.emit({ ...this.filterQuery, ...values, page: 1 });
                }
            });
    }

    /**
     * Filters users based on the provided owner IDs.
     */
    private _filterUsers(userIds: string[]): void {
        const userIdSet = new Set(userIds);

        // Filter users to only those who own jobs
        const filteredUsers = this._allUsers.filter((u) => userIdSet.has(u.id));
        this.activeUsers = filteredUsers.filter((u) => u.active);
        this.otherUsers = filteredUsers.filter((u) => !u.active);

        // Update selected owner for display purposes
        this._updateSelectedOwnerDisplay();
    }

    // Updates the selectedOwner based on the current filterQuery
    private _updateSelectedOwnerDisplay(): void {
        const currentOwner = this.filterQuery?.userId;

        if (!currentOwner) {
            this.selectedOwner = null;
            return;
        }

        const user = this._allUsers.find((u) => u.id === currentOwner);
        this.selectedOwner = user || null;
    }

    /**
     * Called when the client filter changes to update available owners (from client-search component).
     */
    onClientSearchChange(clientId: number | string | undefined): void {
        const numericClientId = clientId ? Number(clientId) : undefined;
        this._clientChanged$.next({ clientId: numericClientId });
    }

    onClientChange(event: Event) {
        const selectedValue = (event.target as HTMLSelectElement).value;

        if (selectedValue === "null" || selectedValue === "") {
            this.filterQuery.clientId = null;
        } else {
            this.filterQuery.clientId = parseInt(selectedValue, 10);
        }

        // Trigger owner refetch for group client dropdown
        // When "All" is selected, use all group client IDs; otherwise use the selected client
        if (this.filterQuery.clientId) {
            this._clientChanged$.next({ clientId: this.filterQuery.clientId });
        } else {
            // "All" selected - use all clients in the group
            const groupClientIds = this.clients?.map(c => c.clientId);
            this._clientChanged$.next({ clientIds: groupClientIds });
        }

        this.filter.emit(this.filterQuery);
    }

    /** Phones, with the filters folded away: what they're set to, in a
     *  line — e.g. "Me · All jobs" or "Acme · All owners · Active jobs". */
    get filtersSummary(): string {
        const q = this.filterQuery;
        if (!q) return '';
        const parts: string[] = [];

        if (q.clientId) {
            const client = [...(this.clients || []), ...this.filterClients[0], ...this.filterClients[1]]
                .find((c) => String(c.clientId) === String(q.clientId));
            if (client) parts.push(client.displayName || client.clientName);
        }

        const owner = q.userId || '';
        if (!owner) {
            parts.push('All owners');
        } else if (owner === this._loginService.userId) {
            parts.push('Me');
        } else {
            const user = this.selectedOwner;
            parts.push(user ? user.fullName || `${user.firstName} ${user.surname}` : '1 owner');
        }

        parts.push(q.active === 'Active' ? 'Active jobs' : q.active === 'Inactive' ? 'Inactive jobs' : 'All jobs');

        return parts.join(' \u00b7 ');
    }

    onClearFilters() {
        if (this.form.controls.clientId) {
            this.form.setValue({
                clientId: '',
                userId: '',
                searchTerm: '',
                active: 'All',
            });
        } else {
            this.form.setValue({
                userId: '',
                searchTerm: '',
                active: 'All',
            });
        }
        // Reset to show all job owners when clearing filters
        // On client group pages, show owners for all clients in the group
        // On main Jobs page, show all job owners
        const groupClientIds = this.clients?.length > 0 ? this.clients.map(c => c.clientId) : undefined;
        this._clientChanged$.next({ clientIds: groupClientIds });
    }

    activechanged($event: any) {
        this.filter.emit(this.filterQuery);
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
    }
}
